import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote, Star, ChevronLeft, ChevronRight } from 'lucide-react';

const testimonials = [
  {
    id: 1,
    name: 'Priya Sharma',
    role: 'Greater Noida ',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80',
    rating: 5,
    text: 'Veda Creation transformed our apartment into a stunning living space. Their attention to detail and understanding of our lifestyle was exceptional. The 3D visualization helped us see the final result before execution.',
  },
  {
    id: 2,
    name: 'Rajesh Mehta',
    role: 'Business Owner, Delhi',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&q=80',
    rating: 5,
    text: 'Outstanding work on our corporate office! The team delivered a modern, functional space that perfectly reflects our brand identity. Professional service from start to finish.',
  },
  {
    id: 3,
    name: 'Pankaj Verma',
    role: 'Restaurant Owner, Delhi',
    // image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&q=80',
    rating: 5,
    text: 'The commercial space design exceeded our expectations. Veda Creation understood the unique requirements of a restaurant and created an ambiance that our customers absolutely love.',
  },
];

export default function Testimonials() {
  const [current, setCurrent] = useState(0);

  const next = () => setCurrent((prev) => (prev + 1) % testimonials.length);
  const prev = () => setCurrent((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  return (
    <section className="py-24 md:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content Side */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-[1px] bg-[#C9A962]" />
                <span className="text-[#C9A962] tracking-[0.3em] text-xs uppercase">Testimonials</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl text-[#2C2C2C] mb-6">
                What Our Clients Say
              </h2>
              <p className="text-[#2C2C2C]/60 text-lg mb-10">
                Hear from the people who trusted us to transform their spaces into something extraordinary.
              </p>
            </motion.div>

            {/* Testimonial Card */}
            <div className="relative">
              <Quote className="absolute -top-4 -left-4 w-16 h-16 text-[#C9A962]/20" />
              
              <AnimatePresence mode="wait">
                <motion.div
                  key={current}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  className="bg-[#FDFBF7] p-8 md:p-10 relative z-10"
                >
                  {/* Rating */}
                  <div className="flex gap-1 mb-6">
                    {[...Array(testimonials[current].rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#C9A962] text-[#C9A962]" />
                    ))}
                  </div>

                  {/* Quote */}
                  <p className="font-serif text-xl text-[#2C2C2C] leading-relaxed mb-8 italic">
                    "{testimonials[current].text}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center gap-4">
                    <img
                      src={testimonials[current].image}
                      alt={testimonials[current].name}
                      className="w-14 h-14 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-serif text-lg text-[#2C2C2C]">
                        {testimonials[current].name}
                      </div>
                      <div className="text-[#2C2C2C]/60 text-sm">
                        {testimonials[current].role}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Navigation */}
              <div className="flex items-center gap-4 mt-8">
                <button
                  onClick={prev}
                  className="w-12 h-12 border border-[#E8E2D9] flex items-center justify-center hover:border-[#C9A962] hover:bg-[#C9A962] hover:text-white transition-all"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={next}
                  className="w-12 h-12 border border-[#E8E2D9] flex items-center justify-center hover:border-[#C9A962] hover:bg-[#C9A962] hover:text-white transition-all"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>

                {/* Dots */}
                <div className="flex gap-2 ml-4">
                  {testimonials.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrent(i)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        i === current ? 'bg-[#C9A962] w-8' : 'bg-[#E8E2D9]'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative hidden lg:block"
          >
            <img
              src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80"
              alt="Happy Clients"
              className="w-full h-[600px] object-cover"
            />
            
            {/* Decorative Frame */}
            <div className="absolute -bottom-6 -right-6 w-full h-full border-2 border-[#C9A962] -z-10" />
            
            {/* Stats Overlay */}
            <div className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur p-6 flex justify-around">
              <div className="text-center">
                <div className="font-serif text-3xl text-[#C9A962]">250+</div>
                <div className="text-[#2C2C2C]/60 text-sm">Happy Clients</div>
              </div>
              <div className="w-[1px] bg-[#E8E2D9]" />
              <div className="text-center">
                <div className="font-serif text-3xl text-[#C9A962]">4.9</div>
                <div className="text-[#2C2C2C]/60 text-sm">Average Rating</div>
              </div>
              <div className="w-[1px] bg-[#E8E2D9]" />
              <div className="text-center">
                <div className="font-serif text-3xl text-[#C9A962]">98%</div>
                <div className="text-[#2C2C2C]/60 text-sm">Would Recommend</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}